"use client";

import { useState, ReactNode, useRef, useEffect } from "react";

interface ChartCarouselProps {
  items: ReactNode[];
}

export function ChartCarousel({ items }: ChartCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [shouldAnimate, setShouldAnimate] = useState(true);
  const [containerHeight, setContainerHeight] = useState<number | null>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const el = slideRefs.current[currentSlide];
    if (!el) return;

    const measure = () => setContainerHeight(el.getBoundingClientRect().height);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();

    return () => {
      ro.disconnect();
    };
  }, [currentSlide, items.length]);

  const goToPrev = () => {
    if (currentSlide === 0) {
      // Jump to last without animation
      setShouldAnimate(false);
      setCurrentSlide(items.length - 1);
      setTimeout(() => setShouldAnimate(true), 50);
    } else {
      setShouldAnimate(true);
      setCurrentSlide((prev) => prev - 1);
    }
  };

  const goToNext = () => {
    if (currentSlide === items.length - 1) {
      // Jump to first without animation
      setShouldAnimate(false);
      setCurrentSlide(0);
      setTimeout(() => setShouldAnimate(true), 50);
    } else {
      setShouldAnimate(true);
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const goToSlide = (index: number) => {
    setShouldAnimate(true);
    setCurrentSlide(index);
  };

  if (items.length === 0) return null;

  return (
    <div className="flex flex-col">
      {items.length > 1 && (
        <div className="flex justify-center items-center gap-4 pb-2">
          {/* Previous Arrow */}
          <button
            type="button"
            onClick={goToPrev}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted/50 transition-all duration-200"
            aria-label="Previous chart"
          >
            <span className="text-foreground text-2xl font-bold leading-none">‹</span>
          </button>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-1.5">
            {items.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => goToSlide(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? "w-6 bg-primary"
                    : "w-1.5 bg-muted-foreground/40 hover:bg-muted-foreground/60"
                }`}
                aria-label={`Go to chart ${index + 1}`}
              />
            ))}
          </div>

          {/* Next Arrow */}
          <button
            type="button"
            onClick={goToNext}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted/50 transition-all duration-200"
            aria-label="Next chart"
          >
            <span className="text-foreground text-2xl font-bold leading-none">›</span>
          </button>
        </div>
      )}

      <div
        className="overflow-hidden relative"
        style={{ height: containerHeight ? `${containerHeight}px` : undefined, transition: 'height 300ms ease' }}
      >
        <div
          className="flex w-full"
          style={{
            transform: `translateX(-${currentSlide * 100}%)`,
            transition: shouldAnimate ? 'transform 300ms ease-out' : 'none'
          }}
        >
          {items.map((item, index) => (
            <div key={index} className="w-full flex-shrink-0">
              <div
                ref={(el) => {
                  slideRefs.current[index] = el;
                }}
                className="w-full"
              >
                {item}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
